import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  Paper,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { Add, Search as SearchIcon } from "@mui/icons-material";

import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    borderRadius: 0,
    backgroundColor: theme.palette.background.paper,
    borderTop: `3px solid ${theme.palette.primary.main}`,
  },

  header: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: theme.spacing(1.25, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    backgroundColor: theme.palette.background.paper,
  },
  headerTitle: {
    flex: 1,
    minWidth: 0,
    fontWeight: 700,
    fontSize: 14.5,
    color: theme.palette.primary.main,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    letterSpacing: "-0.01em",
    lineHeight: 1,
  },

  searchBox: {
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    backgroundColor: theme.palette.background.default,
  },

  pillsRow: {
    display: "flex",
    flexDirection: "row",
    gap: 6,
    padding: theme.spacing(1, 1.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
    flexShrink: 0,
    overflowX: "auto",
    backgroundColor: theme.palette.background.paper,
    "&::-webkit-scrollbar": { display: "none" },
    scrollbarWidth: "none",
    msOverflowStyle: "none",
  },

  listsContainer: {
    flex: 1,
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  },
}));

const TicketsManager = () => {
  const { classes } = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [activeFilter, setActiveFilter] = useState("pending");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [resolvedCount, setResolvedCount] = useState(0);
  const userQueueIds = user.queues.map((q) => q.id);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);

  useEffect(() => {
    if (user.profile.toUpperCase() === "ADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  let searchTimeout;
  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeout);
    if (searchedTerm === "") {
      setSearchParam("");
      return;
    }
    searchTimeout = setTimeout(() => setSearchParam(searchedTerm), 500);
  };

  const isSearching = Boolean(searchParam);

  const pills = [
    { value: "pending", label: i18n.t("ticketsList.pendingHeader"), count: pendingCount > 0 ? pendingCount : null },
    { value: "open", label: i18n.t("ticketsList.assignedHeader"), count: openCount > 0 ? openCount : null },
    { value: "closed", label: i18n.t("tickets.tabs.closed.title"), count: resolvedCount > 0 ? resolvedCount : null },
  ];

  const getListStyle = (listStatus) => activeFilter === listStatus ? {} : { display: "none" };

  return (
    <Paper elevation={0} variant="outlined" className={classes.root}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />

      {/* Header */}
      <Box className={classes.header}>
        <Typography noWrap className={classes.headerTitle}>
          {i18n.t("tickets.tabs.open.title")}
        </Typography>

        <Can
          role={user.profile}
          perform="tickets-manager:showall"
          yes={() => (
            <Tooltip title={i18n.t("tickets.buttons.showAll")} arrow>
              <Switch
                size="small"
                checked={showAllTickets}
                onChange={() => setShowAllTickets((p) => !p)}
                color="primary"
                sx={{ mx: 0 }}
              />
            </Tooltip>
          )}
        />

        <TicketsQueueSelect
          selectedQueueIds={selectedQueueIds}
          userQueues={user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />

        <Tooltip title="New conversation" arrow>
          <IconButton
            size="small"
            onClick={() => setNewTicketModalOpen(true)}
            sx={{
              bgcolor: "primary.main",
              color: "#fff",
              width: 28,
              height: 28,
              borderRadius: 1.5,
              flexShrink: 0,
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            <Add sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search */}
      <Box className={classes.searchBox}>
        <TextField
          inputRef={searchInputRef}
          placeholder={i18n.t("tickets.search.placeholder")}
          size="small"
          fullWidth
          onChange={handleSearch}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 17, color: "text.disabled" }} />
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2.5,
              fontSize: 13,
              backgroundColor: "background.paper",
            },
          }}
        />
      </Box>

      {/* Pill tabs */}
      {!isSearching && (
        <Box className={classes.pillsRow}>
          {pills.map((pill) => {
            const isActive = activeFilter === pill.value;
            return (
              <Button
                key={pill.value}
                onClick={() => setActiveFilter(pill.value)}
                disableElevation
                size="small"
                sx={{
                  flex: 1,
                  borderRadius: 999,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12.5,
                  px: 1,
                  py: 0.5,
                  minWidth: 0,
                  lineHeight: 1.4,
                  gap: 0.75,
                  ...(isActive
                    ? {
                        bgcolor: "primary.main",
                        color: "#fff",
                        "&:hover": { bgcolor: "primary.dark" },
                      }
                    : {
                        bgcolor: "transparent",
                        color: "text.secondary",
                        border: "1px solid",
                        borderColor: "divider",
                        "&:hover": { bgcolor: "action.hover" },
                      }),
                }}
              >
                {pill.label}
                {pill.count !== null && (
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 18,
                      height: 18,
                      borderRadius: 9,
                      fontSize: 10,
                      fontWeight: 700,
                      lineHeight: 1,
                      px: "4px",
                      bgcolor: isActive ? "rgba(255,255,255,0.25)" : "action.selected",
                      color: isActive ? "#fff" : "text.primary",
                    }}
                  >
                    {pill.count > 99 ? "99+" : pill.count}
                  </Box>
                )}
              </Button>
            );
          })}
        </Box>
      )}

      {/* Lists */}
      <Box className={classes.listsContainer}>
        {isSearching ? (
          <TicketsList
            searchParam={searchParam}
            showAll={true}
            selectedQueueIds={selectedQueueIds}
          />
        ) : (
          <>
            <TicketsList
              status="open"
              showAll={showAllTickets}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setOpenCount(val)}
              style={getListStyle("open")}
            />
            <TicketsList
              status="pending"
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setPendingCount(val)}
              style={getListStyle("pending")}
              onAccept={() => setActiveFilter("open")}
            />
            <TicketsList
              status="closed"
              showAll={true}
              selectedQueueIds={selectedQueueIds}
              updateCount={(val) => setResolvedCount(val)}
              style={getListStyle("closed")}
            />
          </>
        )}
      </Box>
    </Paper>
  );
};

export default TicketsManager;
